import React, { useMemo, useState } from 'react';
import Popup from 'reactjs-popup';
import Flatpickr from 'react-flatpickr';
import moment from 'moment-timezone';
import RoomSelector from '../../../shared/RoomSelector';
import client from '../../../shared/api/client';

const FLATPICKR_FORMAT = 'n/j/y at h:iK';

const STEPS = [
  'Event basics',
  'Venue',
  'Date & time',
  'Tickets',
  'Announce & on sale',
  'Review & create',
];

const modalContentStyle = () => {
  const isSmall = window.innerWidth < 768;
  const base = { borderRadius: '12px', border: 'none', padding: 0, overflow: 'hidden' };
  if (isSmall) {
    return { ...base, borderRadius: 0, position: 'fixed', inset: 0, width: 'auto', height: 'auto', maxHeight: '100vh' };
  }
  return { ...base, width: 'min(960px, calc(100vw - 40px))', height: 'min(680px, calc(100vh - 40px))' };
};

const blankTicket = () => ({ name: '', price: '', quantity: '' });
const blankAddOn = () => ({ name: '', price: '' });

const formatCurrency = (raw) => {
  if (raw === '' || raw == null) return '';
  const number = Number(raw);
  if (Number.isNaN(number)) return raw;
  return number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const CurrencyField = ({ value, onChange, currencyLabel }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div className="eb-currency">
      <span className="eb-currency-affix">$</span>
      <input
        className="eb-currency-input"
        inputMode="decimal"
        placeholder="0.00"
        value={focused ? value : formatCurrency(value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))}
      />
      <span className="eb-currency-affix">{currencyLabel}</span>
    </div>
  );
};

const emptyForm = (absorbFeesDefault) => ({
  title: '',
  selectedRooms: [],
  startAt: null,
  doorsAt: null,
  endAt: null,
  userChangedDoors: false,
  userChangedEnd: false,
  ticketTypes: [blankTicket()],
  addOns: [],
  absorbFees: !!absorbFeesDefault,
  isPublic: true,
  announceAt: null,
});

export const toVenueIso = (date, tz) => {
  if (!date || !tz) return null;
  return moment.tz(moment(date).format('YYYY-MM-DD HH:mm'), 'YYYY-MM-DD HH:mm', tz).format();
};

const isPositiveNumber = (value) => value !== '' && Number.isFinite(Number(value)) && Number(value) > 0;
const isNonNegativeNumber = (value) => value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0;

export const isCompleteTicket = (t) => !!t.name && isNonNegativeNumber(t.price) && isPositiveNumber(t.quantity);

export const buildEventBuilderPayload = ({ form, venueOwnership, venueTz, publish }) => {
  const validTickets = form.ticketTypes.filter(isCompleteTicket);
  return {
    event_builder: {
      title: form.title.trim(),
      venue_id: venueOwnership?.venueable_id,
      room_ids: form.selectedRooms.map((r) => r.value),
      start_time: toVenueIso(form.startAt, venueTz),
      door_time: toVenueIso(form.doorsAt, venueTz),
      end_time: toVenueIso(form.endAt, venueTz),
      absorb_fees: form.absorbFees,
      ticket_types: validTickets.map((t) => ({ name: t.name, price: t.price, quantity: t.quantity })),
      add_ons: form.addOns.filter((a) => a.name && a.price !== '').map((a) => ({ name: a.name, price: a.price })),
      publish,
      is_public: form.isPublic,
      // The picked date is the public announce time; "put on sale" always means
      // on sale now, so on_sale_time is left for the server to default to now.
      announce_time: form.isPublic ? toVenueIso(form.announceAt, venueTz) : null,
    },
  };
};

const EventBuilderModal = ({ isOpen, onClose, eventBuilder }) => {
  const { create_url, venue_ownerships = [], rooms = [], absorb_fees_default, currency_label = 'USD' } = eventBuilder || {};
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm(absorb_fees_default));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmingPublish, setConfirmingPublish] = useState(false);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const handleStartChange = (startAt) => setForm((prev) => ({
    ...prev,
    startAt,
    doorsAt: prev.userChangedDoors || !startAt ? prev.doorsAt : moment(startAt).subtract(1, 'hour').toDate(),
    endAt: prev.userChangedEnd || !startAt ? prev.endAt : moment(startAt).add(3, 'hours').toDate(),
  }));

  const venueOwnership = useMemo(() => {
    const roomValue = form.selectedRooms[0]?.value;
    if (!roomValue) return null;
    return venue_ownerships.find((vo) => (vo.room_ids || []).includes(roomValue));
  }, [form.selectedRooms, venue_ownerships]);

  const venueTz = venueOwnership?.tzinfo_identifier;

  const validTickets = form.ticketTypes.filter(isCompleteTicket);

  const stepDone = (index) => {
    switch (index) {
      case 0: return !!form.title.trim();
      case 1: return form.selectedRooms.length > 0 && !!venueTz;
      case 2: return !!form.startAt;
      case 3: return validTickets.length > 0;
      // Announce date is optional — a public event still goes on sale without it.
      default: return true;
    }
  };

  const canContinue = stepDone(step);
  const canCreate = [0, 1, 2, 3, 4].every(stepDone);

  const close = () => {
    setForm(emptyForm(absorb_fees_default));
    setStep(0);
    setError(null);
    setSubmitting(false);
    setConfirmingPublish(false);
    onClose();
  };

  const submit = async (publish) => {
    if (!canCreate || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await client.post(create_url, buildEventBuilderPayload({ form, venueOwnership, venueTz, publish }));
      window.location = data.redirect_url;
    } catch (e) {
      const errors = e.response?.data?.errors;
      setError(errors ? errors.join(', ') : 'Something went wrong creating the event.');
      setSubmitting(false);
    }
  };

  const goNext = () => (step < 5 ? setStep(step + 1) : null);
  const goBack = () => (step > 0 ? setStep(step - 1) : null);

  return (
    <Popup open={isOpen} contentStyle={modalContentStyle()} closeOnDocumentClick={false} className="popup-modal" onClose={close}>
      <div className="eb">
        <style>{`
          .eb {
            --sl-text-primary:#181D27; --sl-text-secondary:#414651; --sl-text-tertiary:#535862; --sl-text-quaternary:#717680;
            --sl-bg-primary:#FFFFFF; --sl-bg-secondary:#FAFAFA; --sl-bg-tertiary:#F5F5F5;
            --sl-border-primary:#D5D7DA; --sl-border-secondary:#E9EAEB; --sl-brand-600:#1982C4;
            --sl-radius-md:8px; --sl-radius-xl:12px;
            display:flex; flex-direction:column; font-family:'Open Sans',sans-serif; color:var(--sl-text-primary);
            background:var(--sl-bg-primary); height:100%; min-height:0; position:relative;
          }
          .eb-confirm-overlay { position:absolute; inset:0; z-index:5; display:flex; align-items:center; justify-content:center; padding:24px; background:rgba(10,13,18,.45); }
          .eb-confirm-card { background:var(--sl-bg-primary); border:1px solid var(--sl-border-secondary); border-radius:var(--sl-radius-xl); padding:24px; max-width:420px; box-shadow:0 12px 24px -6px rgba(10,13,18,.2); }
          .eb-confirm-title { font-size:16px; font-weight:700; margin:0 0 8px; }
          .eb-confirm-body { font-size:14px; color:var(--sl-text-tertiary); margin:0 0 20px; line-height:1.5; }
          .eb-confirm-actions { display:flex; justify-content:flex-end; gap:12px; }
          html[data-theme="dark"] body.od-themed .eb {
            --sl-text-primary:#F5F6F8; --sl-text-secondary:#CDD2DC; --sl-text-tertiary:#A4ABB8; --sl-text-quaternary:#8B92A0;
            --sl-bg-primary:#161B26; --sl-bg-secondary:#1C222E; --sl-bg-tertiary:#22293A;
            --sl-border-primary:#333B4A; --sl-border-secondary:#283040;
          }
          .eb-head { display:flex; justify-content:space-between; align-items:flex-start; padding:20px 24px; border-bottom:1px solid var(--sl-border-secondary); }
          .eb-title { font-size:18px; font-weight:700; margin:0; }
          .eb-sub { font-size:13px; color:var(--sl-text-tertiary); margin:2px 0 0; }
          .eb-close { background:none; border:none; color:var(--sl-text-quaternary); font-size:20px; cursor:pointer; line-height:1; }
          .eb-body { display:flex; flex:1; min-height:0; }
          .eb-rail { width:220px; flex-shrink:0; border-right:1px solid var(--sl-border-secondary); padding:16px; background:var(--sl-bg-secondary); }
          .eb-rail-item { display:flex; align-items:center; gap:10px; padding:8px; border-radius:var(--sl-radius-md); font-size:13px; color:var(--sl-text-tertiary); }
          .eb-rail-item.active { background:var(--sl-bg-primary); color:var(--sl-text-primary); font-weight:600; box-shadow:0 1px 2px rgba(10,13,18,.05); }
          .eb-dot { width:22px; height:22px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:12px; border:1px solid var(--sl-border-primary); flex-shrink:0; }
          .eb-dot.done { background:var(--sl-brand-600); color:#fff; border-color:var(--sl-brand-600); }
          .eb-content { flex:1; padding:24px; overflow-y:auto; min-width:0; }
          .eb-step-title { font-size:16px; font-weight:700; margin:0 0 16px; }
          .eb-field { margin-bottom:16px; }
          .eb-label { display:block; font-size:13px; font-weight:600; margin-bottom:6px; }
          .eb-req { color:#D92D20; }
          .eb-input, .eb-content .form-control { width:100%; padding:8px 12px; border-radius:var(--sl-radius-md); border:1px solid var(--sl-border-primary); background:var(--sl-bg-primary); color:var(--sl-text-primary); font-size:14px; }
          .eb-content input[type=number]::-webkit-inner-spin-button, .eb-content input[type=number]::-webkit-outer-spin-button { -webkit-appearance:none; margin:0; }
          .eb-content input[type=number] { -moz-appearance:textfield; appearance:textfield; }
          .eb-currency { display:flex; align-items:center; gap:8px; padding:8px 12px; border-radius:var(--sl-radius-md); border:1px solid var(--sl-border-primary); background:var(--sl-bg-primary); }
          .eb-currency:focus-within { border-color:var(--sl-brand-600); box-shadow:0 0 0 1px var(--sl-brand-600); }
          .eb-currency-affix { color:var(--sl-text-quaternary); font-size:14px; }
          .eb-currency-input { flex:1; min-width:0; border:none; outline:none; background:transparent; color:var(--sl-text-primary); font-size:14px; text-align:right; }
          .eb-row { display:flex; gap:12px; }
          .eb-row > * { flex:1; }
          .eb-line { display:flex; flex-wrap:wrap; gap:8px; align-items:center; margin-bottom:8px; }
          .eb-line > .eb-input:first-child { flex:1 1 140px; min-width:0; }
          .eb-line .eb-input { margin:0; }
          .eb-add { background:none; border:1px dashed var(--sl-border-primary); color:var(--sl-brand-600); border-radius:var(--sl-radius-md); padding:8px 12px; font-size:13px; font-weight:600; cursor:pointer; }
          .eb-del { background:none; border:none; color:var(--sl-text-quaternary); cursor:pointer; padding:6px; }
          .eb-radio { display:flex; gap:8px; align-items:center; margin-bottom:8px; font-size:14px; }
          .eb-summary { border:1px solid var(--sl-border-secondary); border-radius:var(--sl-radius-md); padding:16px; margin-bottom:12px; }
          .eb-summary h4 { font-size:13px; text-transform:uppercase; letter-spacing:.04em; color:var(--sl-text-quaternary); margin:0 0 8px; }
          .eb-summary-row { display:flex; justify-content:space-between; font-size:14px; padding:2px 0; }
          .eb-foot { display:flex; justify-content:space-between; align-items:center; gap:12px; padding:16px 24px; border-top:1px solid var(--sl-border-secondary); }
          .eb-err { color:#D92D20; font-size:13px; }
          .eb-btn { padding:8px 16px; border-radius:var(--sl-radius-md); font-weight:600; font-size:14px; cursor:pointer; border:1px solid var(--sl-border-primary); background:var(--sl-bg-primary); color:var(--sl-text-secondary); }
          .eb-btn-primary { background:var(--sl-brand-600); border-color:var(--sl-brand-600); color:#fff; }
          .eb-btn:disabled { opacity:.5; cursor:not-allowed; }
          @media (max-width:767px){ .eb-rail { display:none; } }
        `}</style>

        <div className="eb-head">
          <div>
            <h3 className="eb-title">Start an Event</h3>
            <p className="eb-sub">Step {step + 1} of 6 · nothing saves until you create</p>
          </div>
          <button type="button" className="eb-close" onClick={close} aria-label="Close">×</button>
        </div>

        <div className="eb-body">
          <div className="eb-rail">
            {STEPS.map((label, i) => (
              <div key={label} className={`eb-rail-item ${i === step ? 'active' : ''}`}>
                <span className={`eb-dot ${stepDone(i) && i !== step ? 'done' : ''}`}>{stepDone(i) && i !== step ? '✓' : i + 1}</span>
                {label}
              </div>
            ))}
          </div>

          <div className="eb-content">
            {step === 0 && (
              <div>
                <h4 className="eb-step-title">Event basics</h4>
                <div className="eb-field">
                  <label className="eb-label">Event title <span className="eb-req">*</span></label>
                  <input className="eb-input" value={form.title} onChange={(e) => update({ title: e.target.value })} placeholder="e.g. Summer Benefit Concert" autoFocus />
                </div>
              </div>
            )}

            {step === 1 && (
              <div>
                <h4 className="eb-step-title">Venue</h4>
                <div className="eb-field">
                  <label className="eb-label">Venue & room <span className="eb-req">*</span></label>
                  <RoomSelector
                    rooms={rooms}
                    venueOwnerships={venue_ownerships}
                    selectedRooms={form.selectedRooms}
                    onChange={(selected) => update({ selectedRooms: selected || [] })}
                    placeholder="Select a venue room..."
                    menuPortalTarget={document.body}
                  />
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h4 className="eb-step-title">Date & time</h4>
                <div className="eb-field">
                  <label className="eb-label">Event start <span className="eb-req">*</span></label>
                  <div className="flatpickr-date-input px-2 d-flex align-items-center">
                    <div className="pr-2" style={{ fontSize: '18px' }}><i className="far fa-calendar" /></div>
                    <Flatpickr
                      options={{ enableTime: true, dateFormat: FLATPICKR_FORMAT, minDate: new Date(), disableMobile: true }}
                      placeholder="Select date &amp; time"
                      value={form.startAt}
                      onClose={(dates) => handleStartChange(dates[0] || null)}
                    />
                  </div>
                </div>
                <div className="eb-row">
                  <div className="eb-field">
                    <label className="eb-label">Doors</label>
                    <div className="flatpickr-date-input px-2 d-flex align-items-center">
                      <div className="pr-2" style={{ fontSize: '18px' }}><i className="far fa-calendar" /></div>
                      <Flatpickr
                        options={{ enableTime: true, dateFormat: FLATPICKR_FORMAT, disableMobile: true }}
                        placeholder="Select date &amp; time"
                        value={form.doorsAt}
                        onClose={(dates) => update({ doorsAt: dates[0] || null, userChangedDoors: true })}
                      />
                    </div>
                  </div>
                  <div className="eb-field">
                    <label className="eb-label">Event end</label>
                    <div className="flatpickr-date-input px-2 d-flex align-items-center">
                      <div className="pr-2" style={{ fontSize: '18px' }}><i className="far fa-calendar" /></div>
                      <Flatpickr
                        options={{ enableTime: true, dateFormat: FLATPICKR_FORMAT, disableMobile: true }}
                        placeholder="Select date &amp; time"
                        value={form.endAt}
                        onClose={(dates) => update({ endAt: dates[0] || null, userChangedEnd: true })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h4 className="eb-step-title">Tickets</h4>
                {form.ticketTypes.map((t, i) => (
                  <div className="eb-line" key={i}>
                    <input className="eb-input" placeholder="Ticket name" value={t.name} onChange={(e) => {
                      const next = [...form.ticketTypes]; next[i] = { ...t, name: e.target.value }; update({ ticketTypes: next });
                    }} />
                    <div style={{ width: 170, flexShrink: 0 }}>
                      <CurrencyField currencyLabel={currency_label} value={t.price} onChange={(v) => {
                        const next = [...form.ticketTypes]; next[i] = { ...t, price: v }; update({ ticketTypes: next });
                      }} />
                    </div>
                    <input className="eb-input" type="number" min="0" placeholder="Qty" style={{ maxWidth: 90, flexShrink: 0 }} value={t.quantity} onChange={(e) => {
                      const next = [...form.ticketTypes]; next[i] = { ...t, quantity: e.target.value }; update({ ticketTypes: next });
                    }} />
                    {form.ticketTypes.length > 1 && (
                      <button type="button" className="eb-del" onClick={() => update({ ticketTypes: form.ticketTypes.filter((_, j) => j !== i) })}>×</button>
                    )}
                  </div>
                ))}
                <button type="button" className="eb-add" onClick={() => update({ ticketTypes: [...form.ticketTypes, blankTicket()] })}>+ Add ticket type</button>

                <div style={{ marginTop: 24 }}>
                  <label className="eb-label">Add-ons</label>
                  {form.addOns.map((a, i) => (
                    <div className="eb-line" key={i}>
                      <input className="eb-input" placeholder="Add-on name" value={a.name} onChange={(e) => {
                        const next = [...form.addOns]; next[i] = { ...a, name: e.target.value }; update({ addOns: next });
                      }} />
                      <div style={{ width: 170, flexShrink: 0 }}>
                        <CurrencyField currencyLabel={currency_label} value={a.price} onChange={(v) => {
                          const next = [...form.addOns]; next[i] = { ...a, price: v }; update({ addOns: next });
                        }} />
                      </div>
                      <button type="button" className="eb-del" onClick={() => update({ addOns: form.addOns.filter((_, j) => j !== i) })}>×</button>
                    </div>
                  ))}
                  <button type="button" className="eb-add" onClick={() => update({ addOns: [...form.addOns, blankAddOn()] })}>+ Add add-on</button>
                </div>

                <div className="eb-field" style={{ marginTop: 24 }}>
                  <label className="eb-label">Fee handling</label>
                  <label className="eb-radio"><input type="radio" checked={!form.absorbFees} onChange={() => update({ absorbFees: false })} /> Pass fees to buyer</label>
                  <label className="eb-radio"><input type="radio" checked={form.absorbFees} onChange={() => update({ absorbFees: true })} /> Absorb fees</label>
                </div>
              </div>
            )}

            {step === 4 && (
              <div>
                <h4 className="eb-step-title">Announce & on sale</h4>
                <label className="eb-radio"><input type="radio" checked={form.isPublic} onChange={() => update({ isPublic: true })} /> Public</label>
                <label className="eb-radio"><input type="radio" checked={!form.isPublic} onChange={() => update({ isPublic: false })} /> Private</label>
                {form.isPublic && (
                  <div className="eb-field" style={{ marginTop: 12, maxWidth: 320 }}>
                    <label className="eb-label">Announce date &amp; time <span className="eb-currency-affix">(optional)</span></label>
                    <div className="flatpickr-date-input px-2 d-flex align-items-center">
                      <div className="pr-2" style={{ fontSize: '18px' }}><i className="far fa-calendar" /></div>
                      <Flatpickr
                        options={{ enableTime: true, dateFormat: FLATPICKR_FORMAT, minDate: new Date(), disableMobile: true }}
                        placeholder="Select date &amp; time"
                        value={form.announceAt}
                        onClose={(dates) => update({ announceAt: dates[0] || null })}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 5 && (
              <div>
                <h4 className="eb-step-title">Review & create</h4>
                <div className="eb-summary">
                  <h4>Event</h4>
                  <div className="eb-summary-row"><span>{form.title}</span><span>{venueOwnership?.name}</span></div>
                  <div className="eb-summary-row"><span>{form.startAt ? moment(form.startAt).format('ddd, MMM D, YYYY · h:mm A') : ''}</span></div>
                </div>
                <div className="eb-summary">
                  <h4>Tickets</h4>
                  {validTickets.map((t, i) => (
                    <div className="eb-summary-row" key={i}><span>{t.name} × {t.quantity}</span><span>${formatCurrency(t.price)} {currency_label}</span></div>
                  ))}
                </div>
                <div className="eb-summary">
                  <h4>Before it goes public</h4>
                  <div className="eb-summary-row"><span>{form.isPublic ? 'Public' : 'Private'}</span><span>{form.isPublic && form.announceAt ? moment(form.announceAt).format('MMM D, YYYY · h:mm A') : '—'}</span></div>
                </div>
                {error && <p className="eb-err">{error}</p>}
              </div>
            )}
          </div>
        </div>

        <div className="eb-foot">
          <button type="button" className="eb-btn" onClick={step === 0 ? close : goBack}>{step === 0 ? 'Cancel' : 'Back'}</button>
          {step < 5 ? (
            <button type="button" className="eb-btn eb-btn-primary" disabled={!canContinue} onClick={goNext}>Continue</button>
          ) : (
            <div style={{ display: 'flex', gap: 12 }}>
              <button type="button" className="eb-btn" disabled={!canCreate || submitting} onClick={() => submit(false)}>Create without publishing</button>
              <button type="button" className="eb-btn eb-btn-primary" disabled={!canCreate || submitting} onClick={() => setConfirmingPublish(true)}>Create & put on sale</button>
            </div>
          )}
        </div>

        {confirmingPublish && (
          <div className="eb-confirm-overlay">
            <div className="eb-confirm-card">
              <h4 className="eb-confirm-title">Put tickets on sale now?</h4>
              <p className="eb-confirm-body">
                This creates the event and makes tickets available for purchase immediately.
                {form.isPublic && form.announceAt ? ` It will be publicly announced on ${moment(form.announceAt).format('MMM D, YYYY · h:mm A')}.` : ''}
              </p>
              {error && <p className="eb-err">{error}</p>}
              <div className="eb-confirm-actions">
                <button type="button" className="eb-btn" disabled={submitting} onClick={() => setConfirmingPublish(false)}>Cancel</button>
                <button type="button" className="eb-btn eb-btn-primary" disabled={submitting} onClick={() => submit(true)}>{submitting ? 'Creating…' : 'Yes, put on sale'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Popup>
  );
};

export default EventBuilderModal;
